import { z } from 'zod';
import type { ProfileRuntimeSnapshot } from '../container/index';

export const SipEngineBootstrapInputSchema = z.object({
  containerId: z.string().trim().min(1),
  advertisedAddress: z.string().trim().min(1),
  mediaAnchorAddress: z.string().trim().min(1),
  rtpStart: z.number().int().min(1024).max(65535),
  rtpEnd: z.number().int().min(1024).max(65535),
  sipPort: z.number().int().min(1024).max(65535),
  protocol: z.enum(['UDP', 'TCP', 'TLS']),
  bindAddress: z.string().trim().min(1),
  tlsContextId: z.string().uuid().optional(),
  hostId: z.string().uuid().optional(),
  profileName: z.string().min(1).max(255).optional(),
  mediaRealmName: z.string().min(1).max(255).optional(),
  interfaceName: z.string().min(1).max(64).optional(),
  maxConcurrentCalls: z.number().int().positive(),
}).refine((data) => data.rtpStart <= data.rtpEnd, {
  message: 'RTP start port must be less than or equal to RTP end port',
  path: ['rtpEnd'],
}).superRefine((data, ctx) => {
  if (data.protocol === 'TLS' && !data.tlsContextId) {
    ctx.addIssue({ code: 'custom', path: ['tlsContextId'], message: 'Select a TLS context with a valid server certificate for a TLS listener' });
  }
  if (data.protocol !== 'TLS' && data.tlsContextId) {
    ctx.addIssue({ code: 'custom', path: ['tlsContextId'], message: 'TLS context is only valid for TLS listeners' });
  }
});

export type SipEngineBootstrapInput = z.input<typeof SipEngineBootstrapInputSchema>;

export interface SipEngineBootstrapStatus {
  readonly mediaRealms: number;
  readonly sipInterfaces: number;
  readonly containerProfiles: number;
  readonly profileSipInterfaces: number;
  readonly containerInstances: number;
  readonly profileAssignments: number;
  readonly unassignedContainers: number;
  readonly needsBootstrap: boolean;
}

export interface SipEngineBootstrapEntitySummary {
  readonly id: string;
  readonly created: boolean;
}

export interface SipEngineBootstrapSummary {
  readonly container: SipEngineBootstrapEntitySummary & { readonly containerId: string };
  readonly mediaRealm: SipEngineBootstrapEntitySummary & { readonly name: string };
  readonly sipInterface: SipEngineBootstrapEntitySummary & { readonly interfaceId: string };
  readonly containerProfile: SipEngineBootstrapEntitySummary & { readonly name: string };
  readonly profileSipInterface: SipEngineBootstrapEntitySummary;
  readonly profileAssignment: SipEngineBootstrapEntitySummary & { readonly containerId: string; readonly profileId: string };
  readonly snapshot: ProfileRuntimeSnapshot;
}

export function validateSipEngineBootstrapInput(input: SipEngineBootstrapInput): z.output<typeof SipEngineBootstrapInputSchema> {
  return SipEngineBootstrapInputSchema.parse(input);
}
